import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";

const CATEGORY_OPTIONS = [
  { value: "compliance", label: "Compliance" },
  { value: "odborne", label: "Odborné" },
  { value: "interni", label: "Interní" },
  { value: "onboarding", label: "Onboarding" },
  { value: "jine", label: "Jiné" },
];

const POSITION_OPTIONS = [
  { value: "farmaceut", label: "Farmaceut" },
  { value: "asistent", label: "Asistent" },
  { value: "sanitář", label: "Sanitář" },
];

function defaultForm() {
  return {
    code: "",
    title: "",
    description: "",
    providerName: "",
    providerUrl: "",
    category: "jine",
    targetPositions: [],
    validDays: 365,
    reminderDaysBefore: "30,7,1",
    isMandatory: true,
    isActive: true,
    requiresProof: false,
  };
}

function defaultAssignForm() {
  return {
    scopeType: "branch",
    scopeValue: "",
    dueDate: "",
  };
}

export default function TrainingDefinitionsPage() {
  const { user } = useAuth();
  const firma = user?.firma || "";
  const role = user?.role || "";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm());

  const [assignModalItem, setAssignModalItem] = useState(null);
  const [assignForm, setAssignForm] = useState(defaultAssignForm());
  const [assignBusy, setAssignBusy] = useState(false);

  async function loadDefinitions() {
    if (!firma) return;
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`/api/training/${encodeURIComponent(firma)}/definitions`, {
        credentials: "include",
      });
      const data = await res.json().catch(() => []);
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se načíst školení.");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Chyba při načítání.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDefinitions();
  }, [firma]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function updateAssignField(name, value) {
    setAssignForm((prev) => ({ ...prev, [name]: value }));
  }

  function togglePosition(value) {
    setForm((prev) => {
      const exists = prev.targetPositions.includes(value);
      return {
        ...prev,
        targetPositions: exists
          ? prev.targetPositions.filter((x) => x !== value)
          : [...prev.targetPositions, value],
      };
    });
  }

  function startEdit(item) {
    setEditingId(item._id);
    setForm({
      code: item.code || "",
      title: item.title || "",
      description: item.description || "",
      providerName: item.providerName || "",
      providerUrl: item.providerUrl || "",
      category: item.category || "jine",
      targetPositions: Array.isArray(item.targetPositions) ? item.targetPositions : [],
      validDays: item.validDays || 365,
      reminderDaysBefore: Array.isArray(item.reminderDaysBefore)
        ? item.reminderDaysBefore.join(",")
        : "30,7,1",
      isMandatory: !!item.isMandatory,
      isActive: !!item.isActive,
      requiresProof: !!item.requiresProof,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetForm() {
    setEditingId(null);
    setForm(defaultForm());
  }

  function openAssignModal(item) {
    setAssignModalItem(item);
    setAssignForm(defaultAssignForm());
  }

  function closeAssignModal() {
    setAssignModalItem(null);
    setAssignForm(defaultAssignForm());
  }

  function parseReminderDays(value) {
    return String(value || "")
      .split(",")
      .map((x) => Number(String(x).trim()))
      .filter((x) => Number.isFinite(x) && x >= 0);
  }

  async function submitForm(e) {
    e.preventDefault();
    setBusy(true);

    try {
      const payload = {
        ...form,
        validDays: Number(form.validDays) || 365,
        reminderDaysBefore: parseReminderDays(form.reminderDaysBefore),
      };

      const url = editingId
        ? `/api/training/${encodeURIComponent(firma)}/definitions/${editingId}`
        : `/api/training/${encodeURIComponent(firma)}/definitions`;

      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se uložit školení.");

      await loadDefinitions();
      resetForm();
    } catch (err) {
      alert(err.message || "Chyba při ukládání.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(item) {
    try {
      const res = await fetch(
        `/api/training/${encodeURIComponent(firma)}/definitions/${item._id}/active`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isActive: !item.isActive }),
        }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se změnit aktivitu.");

      await loadDefinitions();
    } catch (err) {
      alert(err.message || "Chyba při změně stavu.");
    }
  }

  async function submitAssign() {
    if (!assignModalItem?._id) return;

    if (assignForm.scopeType === "branch" && !String(assignForm.scopeValue || "").trim()) {
      alert("Vyplň pobočku.");
      return;
    }

    if (assignForm.scopeType === "branch" && !String(assignForm.scopeValue || "").trim()) {
      alert("Vyplň pobočku.");
      return;
    }
    setAssignBusy(true);

    try {
      const payload = {
        trainingId: assignModalItem._id,
        dueDate: assignForm.dueDate ? `${assignForm.dueDate}T12:00:00` : null,
      };

      if (assignForm.scopeType === "branch") {
        payload.pharmacyCode = String(assignForm.scopeValue || "").trim();
      } else if (assignForm.scopeType === "region") {
        payload.scopeType = "region";
      } else if (assignForm.scopeType === "firma") {
        payload.scopeType = "firma";
      }

      const res = await fetch(`/api/training/${encodeURIComponent(firma)}/assign`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Nepodařilo se přiřadit školení.");

      const created = Array.isArray(data.created) ? data.created.length : 0;
      const updated = Array.isArray(data.updated) ? data.updated.length : 0;
      const skipped = Array.isArray(data.skipped) ? data.skipped.length : 0;

      alert(
        `Přiřazení dokončeno.\nVytvořeno: ${created}\nAktualizováno: ${updated}\nPřeskočeno: ${skipped}`
      );

      closeAssignModal();
    } catch (err) {
      alert(err.message || "Chyba při přiřazení.");
    } finally {
      setAssignBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Správa školení</h1>
        <p className="mt-1 text-sm text-slate-600">
          Založení a úprava definic školení, které se budou přiřazovat zaměstnancům.
        </p>
      </div>

      <form
        onSubmit={submitForm}
        className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-slate-900">
            {editingId ? "Upravit školení" : "Nové školení"}
          </h2>

          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="rounded-xl border border-slate-300 px-4 py-2 hover:bg-slate-50"
            >
              Zrušit úpravu
            </button>
          )}
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Field label="Kód">
            <input
              value={form.code}
              onChange={(e) => updateField("code", e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
              required
            />
          </Field>

          <Field label="Název">
            <input
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
              required
            />
          </Field>

          <Field label="Kategorie">
            <select
              value={form.category}
              onChange={(e) => updateField("category", e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Poskytovatel">
            <input
              value={form.providerName}
              onChange={(e) => updateField("providerName", e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
            />
          </Field>

          <Field label="Odkaz">
            <input
              value={form.providerUrl}
              onChange={(e) => updateField("providerUrl", e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
            />
          </Field>

          <Field label="Platnost (dní)">
            <input
              type="number"
              min="1"
              value={form.validDays}
              onChange={(e) => updateField("validDays", e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
            />
          </Field>

          <Field label="Připomenutí (dny, čárkou)">
            <input
              value={form.reminderDaysBefore}
              onChange={(e) => updateField("reminderDaysBefore", e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2"
              placeholder="30,7,1"
            />
          </Field>
        </div>

        <Field label="Popis">
          <textarea
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            rows={4}
            className="w-full rounded-xl border border-slate-300 px-3 py-2"
          />
        </Field>

        <div>
          <div className="mb-2 text-sm font-medium text-slate-700">Cílové pozice</div>
          <div className="flex flex-wrap gap-2">
            {POSITION_OPTIONS.map((pos) => {
              const active = form.targetPositions.includes(pos.value);
              return (
                <button
                  key={pos.value}
                  type="button"
                  onClick={() => togglePosition(pos.value)}
                  className={`rounded-full px-3 py-1.5 text-sm border ${
                    active
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-slate-300 hover:bg-slate-50"
                  }`}
                >
                  {pos.label}
                </button>
              );
            })}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Když nic nevybereš, školení bude použitelné pro všechny pozice.
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <CheckBox
            label="Povinné"
            checked={form.isMandatory}
            onChange={(value) => updateField("isMandatory", value)}
          />
          <CheckBox
            label="Aktivní"
            checked={form.isActive}
            onChange={(value) => updateField("isActive", value)}
          />
          <CheckBox
            label="Vyžaduje důkaz"
            checked={form.requiresProof}
            onChange={(value) => updateField("requiresProof", value)}
          />
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={busy}
            className="rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {editingId ? "Uložit změny" : "Vytvořit školení"}
          </button>

          <button
            type="button"
            onClick={resetForm}
            className="rounded-xl border border-slate-300 px-4 py-2 hover:bg-slate-50"
          >
            Vyčistit formulář
          </button>
        </div>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-6 text-slate-600">Načítám…</div>
        ) : error ? (
          <div className="p-6 text-red-600">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-slate-600">Zatím není založeno žádné školení.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Název</th>
                  <th className="px-4 py-3 text-left font-semibold">Kód</th>
                  <th className="px-4 py-3 text-left font-semibold">Kategorie</th>
                  <th className="px-4 py-3 text-left font-semibold">Pozice</th>
                  <th className="px-4 py-3 text-left font-semibold">Platnost</th>
                  <th className="px-4 py-3 text-left font-semibold">Stav</th>
                  <th className="px-4 py-3 text-left font-semibold">Akce</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item._id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{item.title}</div>
                      <div className="text-xs text-slate-500">{item.providerName || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{item.code}</td>
                    <td className="px-4 py-3 text-slate-700">{item.category}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {Array.isArray(item.targetPositions) && item.targetPositions.length > 0
                        ? item.targetPositions.join(", ")
                        : "Všechny"}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{item.validDays} dní</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                          item.isActive
                            ? "bg-green-100 text-green-800 border border-green-200"
                            : "bg-slate-100 text-slate-700 border border-slate-200"
                        }`}
                      >
                        {item.isActive ? "Aktivní" : "Neaktivní"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => openAssignModal(item)}
                          className="rounded-lg bg-blue-600 px-3 py-1.5 text-white hover:bg-blue-700"
                        >
                          Přiřadit
                        </button>

                        <button
                          type="button"
                          onClick={() => startEdit(item)}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
                        >
                          Upravit
                        </button>

                        <button
                          type="button"
                          onClick={() => toggleActive(item)}
                          className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
                        >
                          {item.isActive ? "Deaktivovat" : "Aktivovat"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {assignModalItem && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Přiřadit školení</h2>
                <p className="mt-1 text-sm text-slate-600">{assignModalItem.title}</p>
              </div>

              <button
                type="button"
                onClick={closeAssignModal}
                className="rounded-lg border border-slate-300 px-3 py-1.5 hover:bg-slate-50"
              >
                Zavřít
              </button>
            </div>

            <Field label="Scope">
              <select
                value={assignForm.scopeType}
                onChange={(e) => updateAssignField("scopeType", e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
              >
                <option value="branch">Pobočka</option>
                {role === "rm" && <option value="region">Region</option>}
                {role === "admin" && <option value="firma">Celý LEMON</option>}
              </select>
            </Field>

            {assignForm.scopeType === "branch" && (
              <Field
                label="Pobočka (pharmacyCode)"
              >
                <input
                  value={assignForm.scopeValue}
                  onChange={(e) => updateAssignField("scopeValue", e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2"
                  placeholder="např. 66"
                />
              </Field>
            )}

            <Field label="Termín splnění">
              <input
                type="date"
                value={assignForm.dueDate}
                onChange={(e) => updateAssignField("dueDate", e.target.value)}
                className="w-full rounded-xl border border-slate-300 px-3 py-2"
              />
            </Field>


            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeAssignModal}
                className="rounded-xl border border-slate-300 px-4 py-2 hover:bg-slate-50"
              >
                Zrušit
              </button>

              <button
                type="button"
                onClick={submitAssign}
                disabled={assignBusy}
                className="rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                Přiřadit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-sm font-medium text-slate-700">{label}</div>
      {children}
    </label>
  );
}

function CheckBox({ label, checked, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-slate-300"
      />
      <span>{label}</span>
    </label>
  );
}